import {Component, Input} from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";
import { PageService } from "../shared/page-service";
import { UtilService } from "../shared/util.service";
import { EeocDataV2, EeocFormDataV2, LookupFieldData, SelectOption } from "../../../../typings";
import { TalentProfileApiService } from "../talent-profile-api.service";
import { Observable } from "rxjs";
import { ActivityType, Countries, TalentProfilePage } from "../../../enums";
import { TranslateService } from "@ngx-translate/core";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { Router } from "@angular/router";
import { NOT_A_FIT, sendDoThisLaterEmail } from "../../shared/util/talent-happy-path-util";

@Component({
    selector: 'app-talent-eeoc',
    templateUrl: './talent-eeoc.component.html',
    styleUrl: './talent-eeoc.component.scss',
    standalone: false
})
export class TalentEeocComponent {

  readonly GENDER_OTHER_ID = 8;
  readonly PRONOUNS_OTHER_ID = 5;

  @Input() fromNotAFit: boolean = false;

  countries = Countries;
  talent: Talent;
  posting: PostingInfo;
  talentEeocForm: FormGroup;
  isInitialLoad: boolean = true;
  isFirstEntry: boolean = true;
  genders: LookupFieldData[];
  ethnicityList: LookupFieldData[];
  ageGroupList: LookupFieldData[];
  pronouns: LookupFieldData[];
  homeMarketCountryId = 0;
  selectYesNoOptions: SelectOption[];
  isModalVisible: boolean = false;

  constructor(
    private fb: FormBuilder,
    private pageService: PageService,
    private utils: UtilService,
    private talentProfileApiService: TalentProfileApiService,
    private translate: TranslateService,
    private talentHappyPathDataService: TalentHappyPathDataService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    this.talent = this.talentHappyPathDataService.talent;
    this.posting = this.talentHappyPathDataService.postingInfo;
    this.talentEeocForm = this.fb.group({
      gender: [null],
      lgbtqIdentification: [null],
      ethnicity: [null],
      disabilityIdentification: [null],
      neurodivergentIdentification: [null],
      ageGroup: [null],
      updateSource: ["Talent-Profile"],
      pronouns: [null],
      veteranIdentification: [null],
      customPronoun: [null],
      customGender: [null]
    });

    this.getEeocLookUpFields(this.talent.personId);
    this.loadEeocData(this.talent.personId);
    this.selectYesNoOptions = this.getYesNoList();

    this.talentEeocForm.valueChanges.subscribe((values: EeocDataV2) => {
      if (!this.isInitialLoad) {
        if(this.isFirstEntry) {
          this.talentProfileApiService.createEeocFormEntry(this.talent.personId).subscribe(response => {
            console.log("New form Saved");
            this.isFirstEntry = false;
          }, error => {
            console.error("Error saving new form", error)
          });
        }

        values = this.utils.filterTalentEeoc(values);
        let eeocForm: EeocFormDataV2 = {
          talentId: this.talent.personId,
          eeoc: values,
          homeMarketCountryId: this.homeMarketCountryId
        }

        this.talentProfileApiService.saveEeocData(eeocForm).subscribe(response => {
          console.log("Eeoc data Saved");
        },
          error => {
            console.error('Error saving Eeoc data', error);
          });
      }
    });
  }

  loadEeocData(talent: number): void {
    this.talentProfileApiService.readEeocData(talent).subscribe(eeocData => {
      this.talentEeocForm.patchValue(eeocData.eeoc);
      this.homeMarketCountryId = eeocData.homeMarketCountryId;
      this.isInitialLoad = false;
      this.isFirstEntry = this.isEmptyEeocData(eeocData.eeoc);
    })
  }

  getEeocLookUpFields(talent: number): void {
    this.talentProfileApiService.getEeocLookupFields(talent).subscribe(response => {
      const orderKeyComparator = this.createOrderKeyComparator();

      this.genders = this.sortLookupFieldData(response['gender'], orderKeyComparator);
      this.ethnicityList = this.sortLookupFieldData(response['ethnicity'], orderKeyComparator);
      this.ageGroupList = this.sortLookupFieldData(response['ageGroup'], orderKeyComparator);
      this.pronouns = this.sortLookupFieldData(response['pronouns'], orderKeyComparator);
    });
  }

  private createOrderKeyComparator(): (item1: LookupFieldData, item2: LookupFieldData) => number {
    return (item1: LookupFieldData, item2: LookupFieldData): number => {
      const compare = item1.orderKey - item2.orderKey;
      if (compare !== 0) {
        return compare;
      } else if (item1.description < item2.description) {
        return -1;
      } else if (item2.description < item1.description) {
        return 1;
      }
      return 0;
    };
  }

  private sortLookupFieldData(data: LookupFieldData[], comparator: (item1: LookupFieldData, item2: LookupFieldData) => number): LookupFieldData[] {
    return data.sort(comparator);
  }

  private isEmptyEeocData(eeoc: EeocDataV2): boolean {
    return Object.values(eeoc).every(value => value === null || value === undefined);
  }

  goToEmploymentAuthorization() {
    this.pageService.setActivePage(TalentProfilePage.EMPLOYMENT_AUTHORIZATION)
  }

  getYesNoList(): SelectOption[]{
    return [
      { id: 1, description: this.translate.instant('talent-happy-path.select-list.yes')},
      { id: 2, description: this.translate.instant('talent-happy-path.select-list.no')},
      { id: 3, description: this.translate.instant('talent-happy-path.select-list.no-answer')}
    ];
  }

  finishTalentProfile() {
    this.saveActivity();
  }

  saveActivity() {
    this.talentProfileApiService.createTalentProfileActivity(
      this.talentHappyPathDataService.gather
          ? ActivityType.EEOC_INFORMATION_SUBMITTED
          : ActivityType.TALENT_PROFILE_MODULE_FINISHED,
      this.talent.personId,
      this.posting?.orderId,
      this.posting?.jobPostingId
    ).subscribe(() => {
      console.log('Activity saved');
      if (this.fromNotAFit) {
        this.router.navigate(['/thank-you'], {queryParams: {from: NOT_A_FIT}, skipLocationChange: true});
      } else {
        this.router.navigateByUrl('/thank-you', {skipLocationChange: true});
      }
    }, (error) => {
      console.error('Error saving activity', error);
    });
  }

  doThisLater() {
    if (!this.talentHappyPathDataService.doThisLaterEmailSent) {
      sendDoThisLaterEmail(
        this.posting?.jobPostingId ?? null,
        this.talent.personId,
        this.talent.firstName,
        this.talent.lastName,
        this.talent.email,
        this.posting?.jobTitle ?? null,
        this.posting?.orderId ?? null,
        this.talentProfileApiService,
        this.talentHappyPathDataService.isGeneralApplication
      )
    }
  }

  isFromGather(): boolean {
    if (this.fromNotAFit) {
      return false;
    }
    return this.talentHappyPathDataService.gather && this.talentHappyPathDataService.gather != null;
  }

}
